import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { companyHintParams } from "./companyHint.js";
import { isAuthConfigured } from "./config.js";
import { AuthContext } from "./context.js";
import { getHostAuthSession, HostAuthError, isImpersonationActive, } from "./hostAuth.js";
import { resolveInitialAuthState } from "./session.js";
import { getUserManager } from "./userManager.js";
function initialAuthState() {
    if (!isAuthConfigured()) {
        return { status: "unconfigured" };
    }
    return { status: "loading" };
}
/**
 * Selects the session source once at mount. The host-auth session is configured
 * synchronously at boot (before render) when embedded-mode detection passes, so
 * this branch is stable for the app's lifetime and never flips between renders.
 * When no host session is configured the standalone OIDC provider runs exactly
 * as before (G1: zero impact on the standalone web app).
 */
export function AuthProvider({ children }) {
    const hostSession = getHostAuthSession();
    if (hostSession) {
        return _jsx(HostAuthProvider, { session: hostSession, children: children });
    }
    return _jsx(OidcAuthProvider, { children: children });
}
function hostErrorReason(err) {
    return err instanceof HostAuthError ? err.reason : "refresh_failed";
}
// Proactively refresh a host token this long before it expires, so the in-memory
// user (and `useBearerToken` consumers) never carry a lapsed token and the next
// API call doesn't pay a 401 round-trip.
const HOST_PROACTIVE_REFRESH_BUFFER_SECS = 60;
/**
 * Host-auth provider: seeds the signed-in session from a host-provided token
 * and never redirects to Keycloak. While the initial handshake is in flight the
 * state stays `loading` so the app can hold its boot loader (G2); a failed or
 * timed-out handshake resolves to `hostError`, not a signed-out redirect.
 */
function HostAuthProvider({ session, children, }) {
    const [state, setState] = useState({ status: "loading" });
    // Terminal teardown latch. A desktop logout or terminal error (via subscribe /
    // reportError) can land while a token request is in flight; without this latch
    // the request's success handler would clobber hostError back to signedIn,
    // leaving a signed-in shell over a dead session. Set on teardown, cleared only
    // by an explicit signIn retry, and checked before every promotion to signedIn.
    const tornDown = useRef(false);
    useEffect(() => {
        let cancelled = false;
        const seed = async () => {
            try {
                const token = await session.requestInitialToken();
                if (cancelled || tornDown.current)
                    return;
                setState({ status: "signedIn", user: session.toUser(token) });
            }
            catch (err) {
                if (cancelled)
                    return;
                tornDown.current = true;
                setState({ status: "hostError", reason: hostErrorReason(err) });
            }
        };
        void seed();
        return () => {
            cancelled = true;
        };
    }, [session]);
    // Proactive refresh: while signed in, schedule a host refresh shortly before
    // the current token expires and reschedule off the fresh token. A transient
    // refresh failure is left to the on-demand fetch path; only an explicit
    // host-side logout tears the session down here.
    useEffect(() => {
        if (state.status !== "signedIn")
            return;
        const token = session.getToken();
        if (!token || token.expiresAt === null)
            return;
        let cancelled = false;
        // Impersonation: the one-time code is spent, so there is nothing
        // to refresh with. Rather than let the proactive refresh fail 60s early and
        // leave ~60s of requests going out with a lapsed token (silent 401s), end
        // the session cleanly AT the true expiry, straight to the terminal state
        // that renders the "session ended" screen.
        if (isImpersonationActive()) {
            const msUntilEnd = Math.max((token.expiresAt - Date.now() / 1000) * 1000, 0);
            const endTimer = setTimeout(() => {
                if (!cancelled && !tornDown.current) {
                    tornDown.current = true;
                    setState({ status: "hostError", reason: "refresh_failed" });
                }
            }, msUntilEnd);
            return () => {
                cancelled = true;
                clearTimeout(endTimer);
            };
        }
        const msUntilRefresh = Math.max((token.expiresAt - Date.now() / 1000 - HOST_PROACTIVE_REFRESH_BUFFER_SECS) * 1000, 0);
        const timer = setTimeout(async () => {
            try {
                const refreshed = await session.refresh("expiring");
                if (!cancelled && !tornDown.current) {
                    setState({ status: "signedIn", user: session.toUser(refreshed) });
                }
            }
            catch (err) {
                if (!cancelled && hostErrorReason(err) === "logged_out") {
                    tornDown.current = true;
                    setState({ status: "hostError", reason: "logged_out" });
                }
            }
        }, msUntilRefresh);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [state, session]);
    // React to unsolicited host pushes, chiefly a desktop-side logout the user
    // triggered elsewhere. The host broadcasts it; we tear the session down to a
    // coherent host-error rather than sitting on a dead session or redirecting.
    useEffect(() => {
        return session.subscribe((reason) => {
            tornDown.current = true;
            setState({ status: "hostError", reason });
        });
    }, [session]);
    // Host-auth never calls signinRedirect. A sign-in attempt re-asks the host
    // for a token; sign-out tears down to a host-error the app surfaces coherently.
    const signIn = async () => {
        // Explicit retry: clear the teardown latch so a fresh handshake can promote
        // to signedIn. A teardown arriving during this attempt re-sets it and wins.
        tornDown.current = false;
        setState({ status: "loading" });
        try {
            const token = await session.refresh();
            if (tornDown.current)
                return;
            setState({ status: "signedIn", user: session.toUser(token) });
        }
        catch (err) {
            tornDown.current = true;
            setState({ status: "hostError", reason: hostErrorReason(err) });
        }
    };
    // Embedded sign-out hands control to the host, which owns the real session:
    // the desktop logs out and broadcasts back (→ subscribe → hostError). We also
    // set hostError optimistically so the UI responds immediately rather than
    // waiting on the round-trip.
    const signOut = async () => {
        tornDown.current = true;
        session.requestLogout();
        setState({ status: "hostError", reason: "logged_out" });
    };
    const refresh = async () => {
        try {
            const token = await session.refresh();
            if (tornDown.current)
                return null;
            const user = session.toUser(token);
            setState({ status: "signedIn", user });
            return user;
        }
        catch {
            return null;
        }
    };
    const value = { state, signIn, signOut, refresh };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
function OidcAuthProvider({ children }) {
    const [state, setState] = useState(initialAuthState);
    useEffect(() => {
        if (!isAuthConfigured())
            return;
        const userManager = getUserManager();
        let cancelled = false;
        const load = async () => {
            const next = await resolveInitialAuthState(userManager);
            if (cancelled)
                return;
            setState(next);
        };
        void load();
        const onLoaded = (user) => setState({ status: "signedIn", user });
        const onUnloaded = () => setState({ status: "signedOut" });
        const onSignedOut = () => setState({ status: "signedOut" });
        // automaticSilentRenew fires a hidden iframe before the token expires.
        // If that renewal fails (Keycloak SSO session ended, browser blocking
        // iframe cookies, CSP) oidc-client-ts emits this event and stops
        // retrying. Without handling it the token expires, auto-renew silently
        // gives up, and React state stays signedIn while every API call 401s.
        // Removing the user here fires userUnloaded → onUnloaded → signedOut,
        // so the app surfaces a sign-in prompt instead of a zombie session.
        const onSilentRenewError = () => {
            void userManager.removeUser();
        };
        userManager.events.addUserLoaded(onLoaded);
        userManager.events.addUserUnloaded(onUnloaded);
        userManager.events.addUserSignedOut(onSignedOut);
        userManager.events.addSilentRenewError(onSilentRenewError);
        return () => {
            cancelled = true;
            userManager.events.removeUserLoaded(onLoaded);
            userManager.events.removeUserUnloaded(onUnloaded);
            userManager.events.removeUserSignedOut(onSignedOut);
            userManager.events.removeSilentRenewError(onSilentRenewError);
        };
    }, []);
    const signIn = async (opts) => {
        // returnTo rides oidc-client-ts's own state store, not sessionStorage.
        //
        // That store is deliberately localStorage (see userManager.ts) because a magic-link sign-in starts in one tab and
        // completes in another. sessionStorage is tab-scoped, so the callback tab saw an empty value and the user landed
        // on "/" instead of the deep link they asked for. Handing the value to signinRedirect keeps it in the same store
        // as the PKCE verifier, so it survives the tab hop by construction rather than by a second mechanism that has to
        // be kept in agreement with the first.
        //
        // Keys are spread in only when they have a value: `exactOptionalPropertyTypes` rejects an explicit `undefined`
        // for an optional property, and sending `company=` empty would be worse than omitting it anyway.
        const company = companyHintParams();
        await getUserManager().signinRedirect({
            ...(opts?.returnTo ? { state: { returnTo: opts.returnTo } } : {}),
            ...(company ? { extraQueryParams: company } : {}),
        });
    };
    const signOut = async () => {
        await getUserManager().signoutRedirect();
    };
    const refresh = async () => {
        const um = getUserManager();
        const user = await um.signinSilent().catch(() => null);
        return user ?? null;
    };
    const value = { state, signIn, signOut, refresh };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
/**
 * The deep link the user was trying to reach before being bounced to the IdP, or null.
 *
 * Read from the `User` that `signinRedirectCallback()` resolves with — oidc-client-ts hands back whatever `state` was
 * passed to `signinRedirect`. Nothing to clear: the state entry is single-use and swept with the rest of the flow.
 *
 * Only accepts an app-relative path. `state` round-trips through the IdP, so treating it as opaque and navigating to
 * it unchecked would turn any tampering into an open redirect out of the app.
 */
export function readReturnTo(user) {
    const returnTo = user?.state?.returnTo;
    if (typeof returnTo !== "string")
        return null;
    if (!returnTo.startsWith("/") || returnTo.startsWith("//"))
        return null;
    return returnTo;
}
//# sourceMappingURL=AuthProvider.js.map